import type { ColumnDef } from "@tanstack/react-table";
import _ from "lodash";

import type {
  IExecution,
  IFoundVulnerabilities,
  TForcesExecutionsEdges,
} from "./types";

import { statusFormatter } from "features/vulnerabilities/formatters/status";
import { ExecutionBuildEnum } from "gql/graphql";
import { filterDate } from "utils/date";
import { formatDateTime } from "utils/date";
import { translate } from "utils/translations/translate";

const toTitleCase = (str: string): string =>
  str
    .split(" ")
    .map((item): string => item[0].toUpperCase() + item.slice(1).toLowerCase())
    .join(" ");

const formatBreakBuild = (exitCode: string): string => {
  if (exitCode === "66") {
    return "Yes";
  } else if (exitCode === "0") {
    return "No";
  }

  return "Error";
};
const formatFoundVulnerabilities = (
  vulnerabilities: IExecution["vulnerabilities"],
): IFoundVulnerabilities => {
  if (vulnerabilities === null) {
    return {
      accepted: 0,
      closed: 0,
      open: 0,
      total: 0,
    };
  }

  return {
    accepted: vulnerabilities.numOfAcceptedVulnerabilities ?? 0,
    closed: vulnerabilities.numOfClosedVulnerabilities ?? 0,
    open: vulnerabilities.numOfOpenVulnerabilities ?? 0,
    total:
      (vulnerabilities.numOfAcceptedVulnerabilities ?? 0) +
      (vulnerabilities.numOfOpenVulnerabilities ?? 0) +
      (vulnerabilities.numOfClosedVulnerabilities ?? 0),
  };
};

const formatExecutions = (executions: TForcesExecutionsEdges): IExecution[] => {
  return executions.map((execution): IExecution => {
    const date = formatDateTime(execution.node.date);
    const kind = translate.t(
      `group.forces.kind.${(execution.node.kind ?? "").toLowerCase()}`,
    );
    const strictness = toTitleCase(
      translate.t(
        execution.node.strictness === "lax"
          ? "group.forces.strictness.tolerant"
          : "group.forces.strictness.strict",
      ),
    );
    const { vulnerabilities } = execution.node;
    const foundVulnerabilities: IFoundVulnerabilities = _.isNull(
      vulnerabilities,
    )
      ? {
          accepted: 0,
          closed: 0,
          open: 0,
          total: 0,
        }
      : formatFoundVulnerabilities(vulnerabilities);
    const status = translate.t(
      foundVulnerabilities.open === 0
        ? "group.forces.status.secure"
        : "group.forces.status.vulnerable",
    );
    const breakBuild = formatBreakBuild(execution.node.exitCode ?? "66");

    return {
      ...execution.node,
      breakBuild,
      date,
      foundVulnerabilities,
      kind,
      status,
      strictness,
    };
  });
};

const formatExecutionFilters = (state: string): string => {
  const execFormat: Record<string, string> = {
    breakBuild: "exitCode",
    gitRepo: "gitRepo",
    kind: "type",
    status: "status",
    strictness: "strictness",
  };

  return execFormat[state];
};

const unformatBreakBuild = (breakBuild: string): ExecutionBuildEnum => {
  const unformat: Record<string, ExecutionBuildEnum> = {
    error: ExecutionBuildEnum.Error,
    no: ExecutionBuildEnum.Fixed,
    yes: ExecutionBuildEnum.Failed,
  };

  return unformat[breakBuild.toLowerCase()];
};

const unformatKind = (kind: string): string => {
  const unformat: Record<string, string> = {
    dynamic: "dynamic",
    static: "static",
  };

  return unformat[kind.toLowerCase()];
};

const unformatStrictness = (strictness: string): string => {
  const unformat: Record<string, string> = {
    strict: "strict",
    tolerant: "lax",
  };

  return unformat[strictness.toLowerCase()];
};

const getVulnerabilitySummaries = (
  vulnerabilities: IExecution["vulnerabilities"],
): string => {
  const acceptedTrans = translate.t(
    "group.forces.foundVulnerabilitiesNew.accepted",
  );
  const unManagedTrans = translate.t(
    "group.forces.foundVulnerabilitiesNew.unManaged",
  );
  const totalTrans = translate.t("group.forces.foundVulnerabilitiesNew.total");
  const numOfManagedVulnerabilities =
    vulnerabilities === null
      ? 0
      : (vulnerabilities.numOfManagedVulnerabilities ?? 0);
  const numOfUnManagedVulnerabilities =
    vulnerabilities === null
      ? 0
      : (vulnerabilities.numOfUnManagedVulnerabilities ?? 0);

  const managedStr = `${numOfManagedVulnerabilities} ${acceptedTrans}`;
  const unManagedStr = `${numOfUnManagedVulnerabilities} ${unManagedTrans}`;
  const totalStr = `${numOfManagedVulnerabilities + numOfUnManagedVulnerabilities} ${totalTrans}`;

  return `${managedStr}, ${unManagedStr}, ${totalStr}`;
};

const getColumns = (): ColumnDef<IExecution>[] => {
  return [
    {
      accessorKey: "date",
      filterFn: filterDate,
      header: translate.t("group.forces.date"),
      id: "execution_date",
    },
    {
      accessorKey: "status",
      cell: (cell): JSX.Element => statusFormatter(String(cell.getValue())),
      header: translate.t("group.forces.status.title"),
      id: "status",
    },
    {
      accessorKey: "breakBuild",
      header: translate.t("group.forces.exitCode.title"),
      id: "exit_code",
    },
    {
      accessorFn: (row): number => {
        return row.vulnerabilities?.numOfManagedVulnerabilities ?? 0;
      },
      header: translate.t("group.forces.status.managedVulnerabilities"),
      id: "managed_vulnerabilities",
    },
    {
      accessorFn: (row): number => {
        return row.vulnerabilities?.numOfUnManagedVulnerabilities ?? 0;
      },
      header: translate.t("group.forces.status.unManagedVulnerabilities"),
      id: "unmanaged_vulnerabilities",
    },
    {
      accessorKey: "strictness",
      header: translate.t("group.forces.strictness.title"),
      id: "strictness",
    },
    {
      accessorKey: "kind",
      header: translate.t("group.forces.kind.title"),
      id: "kind",
    },
    {
      accessorFn: (row): string => {
        if (row.gitRepo === "unable to retrieve") {
          return "all roots";
        }

        return row.gitRepo ?? "";
      },
      header: translate.t("group.forces.gitRepo"),
      id: "git_repo",
    },
    {
      accessorFn: (row): string => {
        const EXECUTION_ID_LENGTH = 8;

        return `${String(row.executionId).slice(0, EXECUTION_ID_LENGTH)}...`;
      },
      enableSorting: false,
      header: translate.t("group.forces.identifier"),
    },
  ];
};

export {
  formatExecutionFilters,
  formatExecutions,
  formatFoundVulnerabilities,
  getVulnerabilitySummaries,
  toTitleCase,
  unformatBreakBuild,
  unformatKind,
  unformatStrictness,
  getColumns,
};
